import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, clearCsrfToken, formatApiError, setCsrfToken } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=unknown, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      if (data.user?.role !== "super_admin") {
        setUser(false);
        return;
      }
      setUser(data.user);
    } catch {
      clearCsrfToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      // Never store JWT in localStorage — HttpOnly cookie only.
      if (data.user?.role !== "super_admin") {
        await api.post("/auth/logout").catch(() => {});
        clearCsrfToken();
        return { ok: false, error: "Not a super admin account" };
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      clearCsrfToken();
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearCsrfToken();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
